import { ref } from "vue";
import { defineStore } from "pinia";
import { getPendingChanges } from "@/api/settings";
import { onAdminMutation } from "@/api/client";

const MUTATION_DEBOUNCE_MS = 800;

/**
 * Site-wide "saved but not yet published" tracker behind the page-wide
 * PendingChangesBar. Mirrors the backend's pending-changes summary: every
 * published page / event / media article / document (plus media-group and
 * footer edits) saved since the last rebuild dispatch.
 *
 * Refreshes itself after any mutating admin API call (see onAdminMutation in
 * api/client.js), debounced so a burst of saves coalesces into one fetch.
 */
export const usePendingChangesStore = defineStore("pendingChanges", () => {
  const count = ref(0);
  const items = ref([]);
  const loaded = ref(false);

  let inflight = null;
  let debounceTimer = null;

  async function refresh() {
    if (inflight) return inflight;
    inflight = (async () => {
      try {
        const data = await getPendingChanges();
        count.value = data.count;
        items.value = data.items;
        loaded.value = true;
      } catch {
        // Keep the last known state — the bar simply doesn't update this round.
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  onAdminMutation(() => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(refresh, MUTATION_DEBOUNCE_MS);
  });

  return { count, items, loaded, refresh };
});
